/**
 * footer-accordion.js — Footer accordion sections (mobile only)
 *
 * Features:
 *   • One section open at a time on mobile (<1024px) — except 'contacto'
 *   • Contacto: always visible on mobile, no accordion, no chevron
 *   • Chevron rotation via .is-open class
 *   • aria-expanded accessibility
 *   • Desktop: all sections visible, accordion disabled
 *   • Re-init on resize (mobile ↔ desktop)
 */

const DESKTOP_BREAKPOINT = 1024;
const ALWAYS_VISIBLE = 'contacto';

export function initFooterAccordion() {
	const footer = document.querySelector( '.site-footer' );
	if ( ! footer ) return;

	const sections = Array.from(
		footer.querySelectorAll( '.js-accordion' )
	);
	if ( ! sections.length ) return;

	let openSectionId = null;

	function isMobile() {
		return window.innerWidth < DESKTOP_BREAKPOINT;
	}

	/**
	 * Close an accordion section (hide content, rotate chevron)
	 */
	function closeSection( section ) {
		const content = section.querySelector( '.footer-section__content' );
		const chevron = section.querySelector( '.footer-section__chevron' );
		const btn = section.querySelector( '.footer-section__toggle' );
		if ( content ) {
			content.classList.remove( 'is-open' );
			content.style.display = 'none';
		}
		if ( chevron ) chevron.classList.remove( 'is-open' );
		if ( btn ) btn.setAttribute( 'aria-expanded', 'false' );
	}

	/**
	 * Open an accordion section (show content, rotate chevron)
	 */
	function openAccordion( section ) {
		const content = section.querySelector( '.footer-section__content' );
		const chevron = section.querySelector( '.footer-section__chevron' );
		const btn = section.querySelector( '.footer-section__toggle' );
		if ( content ) {
			content.classList.add( 'is-open' );
			content.style.display = 'block';
		}
		if ( chevron ) chevron.classList.add( 'is-open' );
		if ( btn ) btn.setAttribute( 'aria-expanded', 'true' );
	}

	/**
	 * Set initial visibility state for all sections
	 */
	function initSections() {
		sections.forEach( ( section ) => {
			const sectionId = section.dataset.sectionId;
			const content = section.querySelector( '.footer-section__content' );
			if ( ! content ) return;

			// Contacto: siempre visible en desktop y móvil
			if ( sectionId === ALWAYS_VISIBLE ) {
				content.style.display = '';
				return;
			}

			if ( isMobile() && section.classList.contains( 'js-accordion--open' ) ) {
				openAccordion( section );
				openSectionId = sectionId;
			} else {
				content.style.display = isMobile() ? 'none' : '';
			}
		} );
	}

	// ── Build accordion UI — inject toggle buttons into headings ─────────────
	sections.forEach( ( section ) => {
		const heading = section.querySelector( '.footer-section__heading' );
		const content = section.querySelector( '.footer-section__content' );
		if ( ! heading || ! content ) return;

		let sectionId = 'section';
		section.classList.forEach( ( cls ) => {
			if ( cls.startsWith( 'js-accordion--' ) && cls !== 'js-accordion--open' ) {
				sectionId = cls.replace( 'js-accordion--', '' );
			}
		} );
		section.dataset.sectionId = sectionId;

		const contentId = `footer-content-${ sectionId }`;
		content.id = contentId;

		// A11Y: los títulos de sección NO deben convertirse en <button> en
		// desktop (incumple 4.1.2). Solo en móvil (acordeón real) se usa botón.
		// Guardamos el título original para restaurarlo al pasar a desktop.
		const headingText   = heading.textContent.trim();

		function renderHeading() {
			// Limpiar el heading por completo antes de reconstruirlo.
			while ( heading.firstChild ) heading.removeChild( heading.firstChild );

			// Desktop y "Contacto": el título queda como texto plano (sin botón).
			if ( ! isMobile() || sectionId === ALWAYS_VISIBLE ) {
				heading.textContent = headingText;
				return;
			}

			// ── Móvil: el título vive dentro de un <button> (acordeón). ───────
			// NOTA: no añadimos un nodo de texto separado, solo el botón, para
			// evitar duplicar visualmente el nombre de la sección.
			const btn = document.createElement( 'button' );
			btn.type = 'button';
			btn.className = 'footer-section__toggle';
			btn.setAttribute( 'aria-controls', contentId );
			btn.setAttribute( 'aria-expanded', section.dataset.sectionId === openSectionId ? 'true' : 'false' );
			btn.textContent = headingText;

			const chevron = document.createElement( 'span' );
			chevron.className = 'footer-section__chevron';
			chevron.setAttribute( 'aria-hidden', 'true' );
			chevron.innerHTML = `<svg viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg" style="width:100%;height:100%;stroke:currentColor">
				<path transform="matrix(.69858 -.71553 .69858 .71553 7 9.585)" stroke-width="2" d="M0-1h10.02"/>
				<path transform="matrix(-.69858 -.71553 -.69858 .71553 7 9.585)" stroke-width="2" d="M0-1h10.02"/>
			</svg>`;
			btn.appendChild( chevron );

			btn.addEventListener( 'click', () => {
				if ( ! isMobile() ) return;

				if ( openSectionId === sectionId ) {
					closeSection( section );
					openSectionId = null;
				} else {
					if ( openSectionId ) {
						const prevSection = sections.find(
							( s ) => s.dataset.sectionId === openSectionId
						);
						if ( prevSection ) closeSection( prevSection );
					}
					openAccordion( section );
					openSectionId = sectionId;
				}
			} );

			heading.appendChild( btn );
		}

		section._renderHeading = renderHeading;
		renderHeading();
	} );

	// ── Initial state ───────────────────────────────────────────────────────
	initSections();

	// ── Resize handler (debounced) ──────────────────────────────────────────
	let resizeTimer;
	window.addEventListener( 'resize', () => {
		clearTimeout( resizeTimer );
		resizeTimer = setTimeout( () => {
			// Re-render headings: en desktop los títulos son texto plano (sin
			// <button>); en móvil se reconstruye el botón de acordeón.
			sections.forEach( ( section ) => {
				if ( typeof section._renderHeading === 'function' ) {
					section._renderHeading();
				}
			} );

			if ( isMobile() ) {
				sections.forEach( ( section ) => {
					const sectionId = section.dataset.sectionId;
					// Contacto siempre visible
					if ( sectionId === ALWAYS_VISIBLE ) {
						const content = section.querySelector( '.footer-section__content' );
						if ( content ) content.style.display = '';
						return;
					}
					if ( openSectionId !== sectionId ) {
						closeSection( section );
					}
				} );
			} else {
				// Desktop — show all
				sections.forEach( ( section ) => {
					const sectionId = section.dataset.sectionId;
					if ( sectionId === ALWAYS_VISIBLE ) return;

					const content = section.querySelector( '.footer-section__content' );
					if ( content ) content.style.display = '';
					const chevron = section.querySelector( '.footer-section__chevron' );
					if ( chevron ) chevron.classList.remove( 'is-open' );
					const btn = section.querySelector( '.footer-section__toggle' );
					if ( btn ) btn.setAttribute( 'aria-expanded', 'false' );
				} );
				openSectionId = null;
			}
		}, 100 );
	} );
}
